import { mapGetters, mapState, mapActions } from 'vuex';
import { debounce } from 'lodash';
import tabs from '@/components/generics/tabs/tabs.vue';
import orderBookOffersSushi, { ORDER_BOOK_OFFER_DISPLAY_MODE } from '@/components/purchase/sushi/order-book-sushi/order-book-offers-sushi/order-book-offers-sushi.vue';
import orderBookMyOrdersSushi from '@/components/purchase/sushi/order-book-sushi/order-book-my-orders-sushi/order-book-my-orders-sushi.vue';
import orderBookPricesSushi from '@/components/purchase/sushi/order-book-sushi/order-book-prices-sushi/order-book-prices-sushi.vue';
import orderBookSharesSushi from '@/components/purchase/sushi/order-book-sushi/order-book-shares-sushi/order-book-shares-sushi.vue';
import loaderMixin from '@/mixins/loader';
import log from '@/logger';
import ls from '@/helpers/localStorage';
import { isMarketSuspended, isMarketExpired } from '@/utils/helpers';
import { waitForFirebaseInitialized } from '@/utils/mutex';


const ORDER_BOOK_LS_KEY = 'order-book-contract-id';


const TABS = {
  PRICES: 'prices',
  MY_ORDERS: 'my-orders',
  MY_SHARES: 'my-shares',
  ALL_OFFERS: 'all-offers',
}


const ORDER_BOOK_DEBOUNCE_INTERVAL = 500;
const isPricesDisabled = market => isMarketExpired(market) || isMarketSuspended(market);


export const ORDER_BOOK_DISPLAY_MODES = {
  DEFAULT: 'default', //  all visible, buy/sell offers tabs
  MOBILE: 'mobile', // only one section visible
};

export default {
  mixins: [
    loaderMixin,
  ],
  components: {
    tabs,
    orderBookOffersSushi,
    orderBookMyOrdersSushi,
    orderBookPricesSushi,
    orderBookSharesSushi,
  },
  props: {
    market: {
      type: Object,
      required: true,
    },
    contract: {
      type: Object,
      required: true,
    },
    operationValue: {
      type: String,
      default: 'yes',
      validator: value => ['no', 'yes'].indexOf(value) >= 0,
    },
    operationType: {
      type: String,
      default: 'buy',
    },
    displayMode: {
      type: String,
      default: ORDER_BOOK_DISPLAY_MODES.DEFAULT,
    },
    showTitle: {
      type: Boolean,
      default: false,
    },
    defaultMinimumRows: {
      type: Number,
      default: 0,
    },
    compactCards: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const isInitialised = !isPricesDisabled(this.market);
    return {
      ORDER_BOOK_DISPLAY_MODES,
      ORDER_BOOK_OFFER_DISPLAY_MODE,
      initialised: isInitialised,
      activeTab: TABS.PRICES,
      hasScrollableContent: false,
      showScrollableFade: false,
      TABS,
      viewportHeight: typeof window !== 'undefined' ? (window.visualViewport ? Math.floor(window.visualViewport.height) : window.innerHeight) : 0,
    };
  },
  watch: {
    initialised(value) {
      if (!value) return;
      if (this.tabs.find(tab => tab.name === this.activeTab) === undefined) {
        if (this.tabs.find(tab => tab.name === TABS.PRICES) !== undefined) {
          this.tabChange(TABS.PRICES);
        } else if (this.tabs.length > 0) {
          this.tabChange(this.tabs[0].name);
        }
      }
      this.$emit('initialised');
      this.$nextTick(this.refreshScrollableTableState);
    },
    activeTab() {
      this.$nextTick(this.refreshScrollableTableState);
    },
    orderBook() {
      this.$nextTick(this.refreshScrollableTableState);
    },
    buyOffers() {
      this.$nextTick(this.refreshScrollableTableState);
    },
    sellOffers() {
      this.$nextTick(this.refreshScrollableTableState);
    },
    contractShares() {
      this.$nextTick(this.refreshScrollableTableState);
    },
  },
  computed: {
    ...mapGetters('app', ['isLayoutMobile']),
    ...mapState('trade', ['orderBook', 'contractShares', 'contractOffers']),
    ...mapGetters('user', ['isLoggedIn']),
    classModifiers() {
      return {
        'order-book-sushi': true,
        [`order-book-sushi--${this.displayMode}`]: this.displayMode,
        'order-book-sushi--with-title': this.showTitle,
        'order-book-sushi--card-layout': this.showCardLayout,
        'order-book-sushi--compact-cards': this.showCardLayout && this.compactCards,
        'order-book-sushi--scrollable-active': this.hasScrollableContent,
        'order-book-sushi--scroll-fade': this.showScrollableFade,
      };
    },
    contentModifiers() {
      return {
        'order-book-sushi__content--cards': this.showCardLayout,
      };
    },
    isMobileDisplayMode() {
      return this.displayMode === ORDER_BOOK_DISPLAY_MODES.MOBILE;
    },
    styleVariables() {
      if (this.displayMode !== ORDER_BOOK_DISPLAY_MODES.MOBILE) return {};
      return {
        '--order-book-scrollable-visible-rows': this.minimumScrollableRows,
      };
    },
    minimumScrollableRows() {
      if (this.displayMode !== ORDER_BOOK_DISPLAY_MODES.MOBILE) return 0;
      if (this.viewportHeight > 0 && this.viewportHeight <= 700) return 3;
      return 5;
    },
    minimumRowsForCurrentDisplayMode() {
      if (this.displayMode === ORDER_BOOK_DISPLAY_MODES.MOBILE) {
        return this.minimumScrollableRows;
      }
      if (this.compactCards) {
        return 0;
      }
      return this.defaultMinimumRows;
    },
    isDefaultDisplayMode() {
      return this.displayMode === ORDER_BOOK_DISPLAY_MODES.DEFAULT;
    },
    showCardLayout() {
      return this.isDefaultDisplayMode;
    },
    showCompactCardTitles() {
      return this.showCardLayout && this.compactCards && this.isLayoutMobile;
    },
    showUserCards() {
      return !this.isLayoutMobile || this.isLoggedIn;
    },
    showPricesCard() {
      return this.tabVisible(TABS.PRICES);
    },
    showOrdersCard() {
      return this.tabVisible(TABS.ALL_OFFERS) && this.showUserCards;
    },
    showSharesCard() {
      return this.tabVisible(TABS.MY_SHARES) && this.showUserCards;
    },
    buyOffers() {
      if (this.contract.userOpenOrdersBuyQuantity === 0) return [];
      return this.contractOffers ? this.contractOffers.filter(c => c.tradeType <= 1) : [];
    },
    sellOffers() {
      if (this.contract.userOpenOrdersSellQuantity === 0) return [];
      return this.contractOffers ? this.contractOffers.filter(c => c.tradeType > 1) : [];
    },
    tabs() {
      const tabsAvailable = [];
      if (this.displayMode === ORDER_BOOK_DISPLAY_MODES.MOBILE) {
        if (!isPricesDisabled(this.market)) {
          tabsAvailable.push({ name: TABS.PRICES, title: 'Orderbook' });
        }
        tabsAvailable.push({ name: TABS.MY_ORDERS, title: 'My Orders' });
        tabsAvailable.push({ name: TABS.MY_SHARES, title: 'Ownership' });
      } else {
        if (!isPricesDisabled(this.market)) {
          tabsAvailable.push({ name: TABS.PRICES, title: 'Orderbook' });
        }
        tabsAvailable.push({ name: TABS.MY_SHARES, title: 'Holdings' });
        tabsAvailable.push({ name: TABS.ALL_OFFERS, title: 'Orders' });
      }
      return tabsAvailable;
    },
  },
  methods: {
    ...mapActions('market', ['getMarketContracts']),
    ...mapActions('trade', [
      'getContractShares', 'getContractOffers',
      'updateOrderBookPeriodically', 'clearOrderBookUpdates',
      'getOrderBook', 'setTradeOptions',
    ]),
    tabChange(name) {
      this.activeTab = name;
    },
    updateViewportHeight() {
      this.viewportHeight = window.visualViewport ? Math.floor(window.visualViewport.height) : window.innerHeight;
      this.$nextTick(this.refreshScrollableTableState);
    },
    cardTitleSideModifiers(value) {
      return {
        'order-book-sushi__card-title-side': true,
        'order-book-sushi__card-title-side--active': value === this.operationValue,
      };
    },
    onCompactOrderBookTitleClick(operationValue) {
      if (!this.showCompactCardTitles || operationValue === this.operationValue) return;
      this.setTradeOptions({
        contractId: this.contract.contractId,
        operationType: this.operationType,
        operationValue,
      });
    },
    onScrollableBodyScroll() {
      this.refreshScrollableTableState();
    },
    refreshScrollableTableState() {
      if (!this.$el) return;

      const scrollableBody = this.$el.querySelector('.order-book-sushi__body--scrollable');
      if (this._scrollableBodyNode !== scrollableBody) {
        if (this._scrollableBodyNode) {
          this._scrollableBodyNode.removeEventListener('scroll', this.onScrollableBodyScroll);
        }
        this._scrollableBodyNode = scrollableBody;
        if (this._scrollableBodyNode) {
          this._scrollableBodyNode.addEventListener('scroll', this.onScrollableBodyScroll, { passive: true });
        }
      }

      if (!this._scrollableBodyNode || !this.isMobileDisplayMode) {
        this.hasScrollableContent = false;
        this.showScrollableFade = false;
        return;
      }

      const canScroll = this._scrollableBodyNode.scrollHeight > this._scrollableBodyNode.clientHeight + 1;
      const atBottom = this._scrollableBodyNode.scrollTop + this._scrollableBodyNode.clientHeight >= this._scrollableBodyNode.scrollHeight - 1;
      const tableNode = this._scrollableBodyNode.closest('.order-book-sushi__table--scrollable');
      const helperNode = this.$el.querySelector('.order-book-sushi__footer-cell--price-links, .order-book-offers-sushi__processed-notice, .order-book-my-orders-sushi__processed-notice');
      const helperBleedsBelow = tableNode && helperNode ?
        helperNode.getBoundingClientRect().bottom > tableNode.getBoundingClientRect().bottom + 1 :
        false;
      this.hasScrollableContent = canScroll || helperBleedsBelow;
      this.showScrollableFade = (canScroll && !atBottom) || helperBleedsBelow;
    },
    tabVisible(name) {
      if (this.displayMode === ORDER_BOOK_DISPLAY_MODES.MOBILE) {
        return this.activeTab === name;
      }
      const names = this.tabs.map(i => i.name);
      return !!this.orderBook && names.includes(name);
    },
    refreshOrderBook() {
      if (!this.isLoggedIn) {
        this.isLoading = false;
        return;
      }
      this.debouncedRefreshOrderBook();
    },
    offerCanceled() {
      this.isLoading = true;
      this.refreshOrderBook();
      this.getMarketContracts(this.market.marketId);
    },
  },
  created() {
    log.info('[ORDER BOOK]: reinit');
    this.debouncedRefreshOrderBook = debounce(() => {
      if (!this.isLoggedIn) {
        log.info('[ORDER BOOK]: api - load contracts and shares (not logged in)');
        this.isLoading = false;
        return;
      }
      log.info('[ORDER BOOK]: api - load contracts and shares (logged in)');
      const currentContractId = this.contract.contractId;

      const promises = [];

      if (
        this.contract.userOpenOrdersBuyQuantity > 0 ||
        this.contract.userOpenOrdersSellQuantity > 0) {
        log.info('[ORDER BOOK] will fetch offers');
        promises.push(this.getContractOffers(this.contract.contractId));
      } else {
        log.info('[ORDER BOOK] should clear offers');
        this.getContractOffers(null);
      }

      if (this.contract.userQuantity > 0) {
        log.info('[ORDER BOOK] will fetch shares');
        promises.push(this.getContractShares(this.contract.contractId));
      } else {
        log.info('[ORDER BOOK] should clear shares');
        this.getContractShares(null);
      }

      log.info(`[ORDER BOOK]: calling api - ${promises.length} promises.`);
      if (promises.length > 0) {
        Promise.all(promises)
          .then(() => {
            ls.set(ORDER_BOOK_LS_KEY, currentContractId);
            this.initialised = true;
            this.isLoading = false;
          })
          .catch(() => {
            ls.set(ORDER_BOOK_LS_KEY, null);
            this.initialised = true;
            this.isLoading = false;
          });
      } else {
        this.isLoading = false;
        ls.set(ORDER_BOOK_LS_KEY, currentContractId);
        this.initialised = true;
      }
    }, ORDER_BOOK_DEBOUNCE_INTERVAL);

    this.debouncedGetOrderBook = debounce(() => {
      log.info('[ORDER BOOK]: api - load order book');
      this.getOrderBook(this.contract.contractId);
    }, ORDER_BOOK_DEBOUNCE_INTERVAL);
  },
  mounted() {
    this.isLoading = true;
    window.addEventListener('resize', this.updateViewportHeight);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.updateViewportHeight);
    }
    waitForFirebaseInitialized()
      .then(() => {
        if (this.isLoggedIn) {
          this.debouncedGetOrderBook();
        }
        this.updateOrderBookPeriodically(this.contract.contractId);
        this.refreshOrderBook();
        this.$nextTick(this.refreshScrollableTableState);
      });
  },
  updated() {
    this.refreshScrollableTableState();
  },
  beforeDestroy() {
    this.debouncedRefreshOrderBook.cancel();
    this.debouncedGetOrderBook.cancel();
    this.clearOrderBookUpdates(this.contract.contractId);
    window.removeEventListener('resize', this.updateViewportHeight);
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.updateViewportHeight);
    }
    if (this._scrollableBodyNode) {
      this._scrollableBodyNode.removeEventListener('scroll', this.onScrollableBodyScroll);
    }
  },
};



// WEBPACK FOOTER //
// ./src/components/purchase/sushi/order-book-sushi/order-book-sushi.js